import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { ApiErrorResponse } from '../types/api.types'
import { store } from '../store/store'
import { updateAccessToken, updateProfile, updateRefreshToken } from '../store/profile-data.slice'

interface RequestOptions {
  retryCount?: number
}

export class AxiosService {
  private static instance: AxiosService
  private baseUrl = process.env.REACT_APP_BASE_URL
  private accessToken: string | null = null
  private refreshToken: string | null = null
  private maxRetries = 0
  private axiosInstance = axios.create({
    baseURL: this.baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 10000,
    // withCredentials: true,
  })

  private constructor() {
    const accessTokenFromStorage = localStorage.getItem('accessToken') || null
    const refreshTokenFromStorage = localStorage.getItem('refreshToken') || null

    this.setAccessToken(accessTokenFromStorage)
    this.setRefreshToken(refreshTokenFromStorage)
  }

  getTokens() {
    return {
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
    }
  }

  public static getInstance(): AxiosService {
    if (!AxiosService.instance) {
      AxiosService.instance = new AxiosService()
    }
    return AxiosService.instance
  }

  get<T>(url: string, data: any): Promise<AxiosResponse<T>> {
    const apiUrl = this.baseUrl + url

    return axios.get(apiUrl, {
      params: {
        ...data,
      },
    })
  }

  post<T>(url: string, data: any): Promise<AxiosResponse<T>> {
    const apiUrl = this.baseUrl + url
    return axios.post(apiUrl, data)
  }

  put<T>(url: string, data: any, contentType: string): Promise<AxiosResponse<T>> {
    return axios.put(url, data, {
      headers: { 'Content-Type': contentType },
    })
  }

  async postWithToken<T>(
    url: string,
    data: any,
    token?: string,
    options: RequestOptions = {
      retryCount: 0,
    },
  ): Promise<AxiosResponse<T>> {
    const apiUrl = this.baseUrl + url
    try {
      const res = await this.axiosInstance.post(apiUrl, data, {
        withCredentials: true,
      })
      return res
    } catch (error) {
      const { doRetry } = await this.handleError(error, options)

      if (doRetry) {
        throw error

        // return this.postWithToken(url, data, token, {
        //   ...options,
        //   retryCount: options?.retryCount ? options.retryCount + 1 : 1,
        // })
      }

      throw error
    }
  }

  async getWithToken<T>(
    url: string,
    data?: any,
    token?: string,
    options: RequestOptions = {
      retryCount: 0,
    },
  ): Promise<AxiosResponse<T>> {
    const apiUrl = this.baseUrl + url
    try {
      const res = await this.axiosInstance.get(apiUrl, {
        params: {
          ...data,
        },
      })
      return res
    } catch (error) {
      const { doRetry } = await this.handleError(error, options)

      if (doRetry) {
        return this.getWithToken(url, data, token, {
          ...options,
          retryCount: options?.retryCount ? options.retryCount + 1 : 1,
        })
      }

      throw error
    }
  }

  async putWithToken<T>(
    url: string,
    data: any,
    token?: string,
    options: RequestOptions = {
      retryCount: 0,
    },
  ): Promise<AxiosResponse<T>> {
    try {
      const res = await this.axiosInstance.put(url, data)
      return res
    } catch (error) {
      const { doRetry } = await this.handleError(error, options)

      if (doRetry) {
        return this.putWithToken(url, data, token, {
          ...options,
          retryCount: options?.retryCount ? options.retryCount + 1 : 1,
        })
      }

      throw error
    }
  }

  private async handleError(
    _error: any,
    options: RequestOptions = {
      retryCount: 0,
    },
  ): Promise<{
    doRetry: boolean
  }> {
    try {
      const error = _error as AxiosError<ApiErrorResponse>
      console.error('error handleError: ', error)

      if (options?.retryCount && options.retryCount >= this.maxRetries) {
        // Throw the original error when max retries are reached
        throw error
      }

      if (error.response?.status === 401) {
        await this.getUpdatedTokens()

        await this.delay(Math.pow(2, options?.retryCount || 0) * 1000)
        return {
          doRetry: true,
        }
      }

      if (error.response?.status && [0, 500, 502, 503, 504].includes(error.response?.status)) {
        return {
          doRetry: true,
        }
      }

      // Fallback: Throw the original error
      throw error
    } catch (_error2) {
      const error = _error as AxiosError<ApiErrorResponse>
      const error2 = _error2 as AxiosError<ApiErrorResponse>

      console.error('error while retrying: ', error2)

      if (error?.response?.status === 401) {
        // Upon retry failure for 401, logout the user
        this.clearTokens()
      }

      throw error2
    }
  }

  setAccessToken(accessToken: string | null) {
    this.accessToken = accessToken

    if (accessToken) {
      this.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken
    } else {
      this.axiosInstance.defaults.headers.common['Authorization'] = null
    }

    // Old legacy logic, kept for backwards compatibility
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken)
    } else {
      localStorage.removeItem('accessToken')
    }
    store.dispatch(updateAccessToken(accessToken))
  }
  setRefreshToken(refreshToken: string | null) {
    this.refreshToken = refreshToken

    // Old legacy logic, kept for backwards compatibility
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    } else {
      localStorage.removeItem('refreshToken')
    }
    store.dispatch(updateRefreshToken(refreshToken))
  }

  clearTokens() {
    this.setAccessToken(null)
    this.setRefreshToken(null)

    store.dispatch(updateProfile(null))
    localStorage.removeItem('@user')
  }

  async getUpdatedTokens() {
    const response = await this.axiosInstance.post<{
      accessToken: string
    }>(this.baseUrl + '/auth/token/refresh', {
      refreshToken: this.refreshToken,
    })

    this.setAccessToken(response.data.accessToken)
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
